import { useLocalOrderId } from '@libs/client'
import {
  capturePostHogEvent,
  identifyPostHog,
  initPostHog,
  registerPostHogContext
} from '@ui/analytics'
import { useRouter } from 'next/router'
import { useEffect, useMemo } from 'react'
import { useAppBuild } from './useAppBuild'
import { useCheckoutFromSource } from './useCheckoutFromSource'
import { usePageId } from './usePageId'
import { useSessionId } from './useSessionId'
import { useUserId } from './useUserId'

/** PostHog is scoped to the two funnels it was integrated for. */
const TRACKED_ROUTES = ['/checkout', '/cartPage']

/**
 * Boots PostHog on the checkout and cart pages and keeps the identity and super
 * properties in sync with the query params the mobile app hands over.
 *
 * Everything happens in effects, so nothing is done during render. That is only
 * safe because the analytics module buffers: this hook lives in `_app`, and React
 * fires a child's effects *before* its parent's, so the pages' own effects
 * capture `cart_page_loaded` / `checkout_page_loaded` before the calls below run.
 * Those events queue up and are replayed after identity is applied - see the
 * two-queue note in `src/analytics/posthog.ts`.
 */
export const usePostHogTracking = () => {
  const router = useRouter()
  const sessionId = useSessionId()
  const userId = useUserId()
  const pageId = usePageId()
  const source = useCheckoutFromSource()
  const [appBuild] = useAppBuild()
  const [cartId] = useLocalOrderId()

  const isTracked = TRACKED_ROUTES.includes(router.pathname)

  const context = useMemo(
    () => ({
      session_id: sessionId,
      sellpage_id: pageId,
      checkout_source: source,
      app_build: appBuild ?? '',
      cart_id: cartId ?? ''
    }),
    [sessionId, pageId, source, appBuild, cartId]
  )

  useEffect(() => {
    if (!isTracked) return
    initPostHog()
  }, [isTracked])

  // Re-runs as the mobile app's values arrive: `appBuild` and `cartId` come from
  // global state and are usually still empty on the first commit.
  useEffect(() => {
    if (!isTracked) return
    registerPostHogContext(context)
    identifyPostHog(userId)
  }, [isTracked, context, userId])

  useEffect(() => {
    if (!isTracked) return
    capturePostHogEvent('$pageview')
  }, [isTracked, router.pathname])
}
